import React, { useEffect, useRef, useState } from 'react';

import { soundService } from '../../services/soundService';

const ALPHABET = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L', 'M', 'N', 'O', 'P', 'Q', 'R', 'S', 'T', 'U', 'V', 'W', 'X', 'Y', 'Z'];

interface WheelProps {
  size?: number;
  selected: string;
  spinning?: boolean;
  /**
   * Idle drift — the wheel turns slowly (~1 rpm) while nothing is happening.
   * Used by the logged-out landing hero, where the wheel is the product demo
   * and a dead disc reads as a screenshot. OFF by default, so the in-game
   * spin screen is byte-identical to what it was.
   *
   * Honours `prefers-reduced-motion` and pauses on hover/focus (the caller
   * can also just stop passing it).
   */
  idle?: boolean;
  /** Visual variant — keeps room to vary the wheel's chrome later. */
  variant?: 'classic';
}

/**
 * Letter wheel for the spin screen. 26 letters around a ring, pointer at top.
 * When `spinning`, rotates rapidly and blurs slightly. When stopped, settles
 * with the `selected` letter at the pointer position.
 */
export function Wheel({ size = 280, selected, spinning, idle }: WheelProps) {
  const radius = size / 2;
  const innerRadius = radius - 38;
  const idx = Math.max(0, ALPHABET.indexOf(selected.toUpperCase()));

  // We rotate the whole disc so `selected` lands at the top.
  const targetAngle = -(idx / 26) * 360;
  const [angle, setAngle] = useState(targetAngle);

  const reduced =
    typeof window !== 'undefined' &&
    !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
  const drifting = !!idle && !spinning && !reduced;

  useEffect(() => {
    if (spinning || drifting) return;
    setAngle(targetAngle);
  }, [targetAngle, spinning, drifting]);

  // Spin animation: when spinning becomes true, accelerate; when it stops,
  // CSS transition + final angle handles the deceleration.
  useEffect(() => {
    if (!spinning) return;
    let raf = 0;
    let a = angle;
    const tick = () => {
      a -= 12;
      setAngle(a % 360);
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [spinning]);

  /* ---- Sound: the ritual opener ----------------------------------------
     The 2.3s ratchet starts with the wheel and the wooden thock lands with the
     letter. Both live HERE, on the wheel's own state transitions, rather than
     in the screen that renders it — so the wheel is audible everywhere it
     spins, and it cannot get out of sync with its own animation.

     The idle drift on the landing hero is deliberately silent: it is wallpaper,
     and wallpaper that whirrs at you is an advert.

     Both are SHARED sounds — if a TV has the room, the service suppresses them
     on the phones and the TV plays them. */
  const spunRef = useRef(false);
  useEffect(() => {
    if (spinning) {
      // ONLY on the false→true edge. The letter arrives mid-spin (`selected`
      // fills in a beat after `spinning` goes true), and keying this effect on
      // both would start a SECOND 2.3s whirr on top of the first — two
      // ratchets, slightly out of phase, which is exactly the "six phones"
      // problem in miniature, on one device. Caught by driving a real game.
      if (spunRef.current) return;
      spunRef.current = true;
      soundService.spin();
      return;
    }
    // Only on a real stop — not on the initial mount of an already-settled wheel.
    if (spunRef.current && selected) {
      spunRef.current = false;
      soundService.reveal(); // cuts the whirr short + a 10ms haptic
    }
  }, [spinning, selected]);

  // Idle drift — 6°/s, i.e. one revolution a minute. Slow enough that you
  // only notice it if you look; alive enough that the page isn't a poster.
  useEffect(() => {
    if (!drifting) return;
    let raf = 0;
    let prev = performance.now();
    const tick = (now: number) => {
      const dt = now - prev;
      prev = now;
      setAngle(a => (a - (dt / 1000) * 6) % 360);
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [drifting]);

  return (
    <div
      style={{
        position: 'relative',
        width: size,
        height: size,
      }}
    >
      {/* Pointer at top */}
      <div
        style={{
          position: 'absolute',
          top: -8,
          left: '50%',
          transform: 'translateX(-50%)',
          width: 0,
          height: 0,
          borderLeft: '12px solid transparent',
          borderRight: '12px solid transparent',
          borderTop: '18px solid var(--accent)',
          zIndex: 3,
          filter: 'drop-shadow(0 4px 6px rgba(255,90,31,0.4))',
        }}
      />
      {/* Outer glow */}
      <div
        style={{
          position: 'absolute',
          inset: -10,
          borderRadius: '50%',
          background: spinning
            ? 'radial-gradient(circle, rgba(255,90,31,0.18), transparent 70%)'
            : 'radial-gradient(circle, rgba(255,90,31,0.08), transparent 70%)',
          transition: 'background 0.4s ease',
        }}
      />
      {/* The disc */}
      <div
        style={{
          position: 'relative',
          width: '100%',
          height: '100%',
          borderRadius: '50%',
          background: 'linear-gradient(155deg, var(--surface), var(--bg-2))',
          boxShadow: '0 24px 48px -16px rgba(22,22,26,0.18), inset 0 0 0 1px var(--line)',
          transform: `rotate(${angle}deg)`,
          // A 1.6s ease on a value we're re-setting every frame would smear
          // the drift into mush. The settle transition only applies when the
          // wheel is coming to REST on a letter.
          transition: (spinning || drifting) ? 'none' : 'transform 1.6s cubic-bezier(0.18, 0.9, 0.22, 1)',
          filter: spinning ? 'blur(0.5px)' : 'none',
        }}
      >
        {ALPHABET.map((letter, i) => {
          const a = (i / 26) * 360;
          // Nothing is "selected" while the wheel is drifting or empty —
          // otherwise the landing hero would sit there quietly highlighting
          // an A it never picked.
          const isSelected = i === idx && !spinning && !drifting && !!selected;
          return (
            <div
              key={letter}
              style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: `rotate(${a}deg) translateY(-${innerRadius}px) rotate(-${a}deg)`,
                transformOrigin: 'center',
                fontFamily: 'var(--display)',
                fontWeight: isSelected ? 800 : 600,
                fontSize: isSelected ? 22 : 16,
                color: isSelected ? 'var(--accent)' : 'var(--ink-3)',
                width: 28,
                height: 28,
                marginLeft: -14,
                marginTop: -14,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: spinning ? 'none' : 'all 0.3s ease',
                letterSpacing: '-0.02em',
              }}
            >
              {letter}
            </div>
          );
        })}
      </div>
      {/* Center hub showing the selected letter */}
      <div
        style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: size * 0.42,
          height: size * 0.42,
          borderRadius: '50%',
          background: spinning
            ? 'linear-gradient(135deg, var(--accent-2), var(--accent))'
            // --hero-grad, not a hardcoded white→peach: the literal
            // #FFFFFF rendered as a white puck on the dark canvas.
            : 'var(--hero-grad)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontFamily: 'var(--display)',
          fontWeight: 800,
          fontSize: size * 0.22,
          color: spinning ? 'white' : 'var(--ink)',
          letterSpacing: '-0.04em',
          boxShadow: '0 8px 24px -8px rgba(22,22,26,0.20), inset 0 0 0 1px var(--line)',
          zIndex: 2,
          transition: 'all 0.3s ease',
          filter: spinning ? 'blur(6px)' : 'none',
        }}
      >
        {/* No letter yet (the landing hero before its first spin) reads the
            same as mid-spin: a question mark. That IS the invitation. */}
        {spinning || !selected ? '?' : selected}
      </div>
    </div>
  );
}
